import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Dropdown from '../../../../components/Dropdown';
import Toast from '../../../../components/Toast';
import CustomizedSwitch from '../../../../components/Switch';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import ConfigKeyService from '../../../../services/config-key.service';
import ProductService from '../../../../services/product.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';

interface ConfigKeyRow {
  id: number;
  key_code: string;
  display_label: string;
  data_type: string;
  scope: string;
  product_id: number | null;
  is_mandatory: boolean;
}

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

const ConfigKeyList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'key_code' });
  const [items, setItems] = useState<ConfigKeyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ConfigKeyRow | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [products, setProducts] = useState<ProductOption[]>([]);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
  }, []);

  const { loading } = useFetch({
    service: ConfigKeyService,
    func: 'list',
    payload: {
      includeDeleted: showInactive,
      page,
      limit: perPage,
      scope: scopeFilter || undefined,
      productId: productFilter ? Number(productFilter) : undefined,
    },
    dependency: [showInactive, page, perPage, scopeFilter, productFilter, refreshToken],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const productLabel = useCallback(
    (productId: number | null) => {
      if (!productId) return '—';
      const product = products.find((p) => p.id === productId);
      return product ? product.product_code : productId;
    },
    [products]
  );

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.masters}${AuthRoutes.configKeys}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const response: any = await ConfigKeyService.remove(deleteTarget.id);
      const softOrHard = response?.data?.softOrHard;
      toast.success(
        <Toast
          message={
            softOrHard === 'soft'
              ? t('masters.configKeys.list.pageTexts.deleteSuccessSoft')
              : t('masters.configKeys.list.pageTexts.deleteSuccessHard')
          }
        />,
        { icon: false }
      );
      setRefreshToken((v) => v + 1);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, t]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'key_code', sortEnabled: true, width: '18%', label: t('masters.configKeys.list.pageTexts.table.columns.code') },
      { id: 'display_label', sortEnabled: true, width: '22%', label: t('masters.configKeys.list.pageTexts.table.columns.label') },
      { id: 'data_type', sortEnabled: true, width: '12%', label: t('masters.configKeys.list.pageTexts.table.columns.dataType') },
      { id: 'scope', sortEnabled: true, width: '12%', label: t('masters.configKeys.list.pageTexts.table.columns.scope') },
      { id: 'product_id', sortEnabled: true, width: '13%', label: t('masters.configKeys.list.pageTexts.table.columns.product') },
      { id: 'is_mandatory', sortEnabled: true, width: '13%', label: t('masters.configKeys.list.pageTexts.table.columns.mandatory') },
      { id: 'actions', sortEnabled: false, width: '10%', label: t('masters.configKeys.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: ConfigKeyRow; className?: string }) => (
      <tr className={className}>
        <td>{item.key_code}</td>
        <td>{item.display_label}</td>
        <td>{item.data_type}</td>
        <td>{item.scope}</td>
        <td>{item.scope === 'product' ? productLabel(item.product_id) : '—'}</td>
        <td>
          <StatusBadge
            label={
              item.is_mandatory
                ? t('masters.configKeys.list.pageTexts.mandatoryYes')
                : t('masters.configKeys.list.pageTexts.mandatoryNo')
            }
            colour={item.is_mandatory ? '#ed6c02' : '#757575'}
          />
        </td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => handleEdit(item.id),
              },
              {
                id: 'delete',
                label: t('common.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: () => setDeleteTarget(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [handleEdit, productLabel, t]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('masters.configKeys.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('masters.configKeys.list.pageTexts.title')}</h4>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('masters.configKeys.list.pageTexts.showInactive')}
            name="showInactive"
            checked={showInactive}
            onChange={() => setShowInactive((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('masters.configKeys.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.configKeys}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-3">
          <Dropdown
            name="scopeFilter"
            value={scopeFilter}
            placeholder={t('masters.configKeys.list.pageTexts.filters.allScopes')}
            options={[
              { id: 'global', name: 'Global' },
              { id: 'product', name: 'Product' },
              { id: 'tenant', name: 'Tenant' },
            ]}
            noSearch
            onChange={(e) => setScopeFilter(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <Dropdown
            name="productFilter"
            value={productFilter}
            placeholder={t('masters.configKeys.list.pageTexts.filters.allProducts')}
            options={products.map((p) => ({ id: String(p.id), name: p.product_code }))}
            noSearch
            onChange={(e) => setProductFilter(e.target.value)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('masters.configKeys.list.pageTexts.deleteDialog.title')}
        content={t('masters.configKeys.list.pageTexts.deleteDialog.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('masters.configKeys.list.pageTexts.deleteDialog.cancel')}
              onClick={() => setDeleteTarget(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('masters.configKeys.list.pageTexts.deleteDialog.confirm')}
              onClick={handleDeleteConfirm}
            />
          </>
        }
      />
    </>
  );
};

export default ConfigKeyList;
